import React from 'react';
import ReactApexChart from 'react-apexcharts';
import { Box, Typography } from '@mui/material';
import { resolveSlotValues, getDTWidgetSlots } from '../../registry/dtWidgetTypes';
import { useDTDemoMap } from '../../hooks/useDTDemoTick';
import { hasLiveSlotValue } from '../../utils/dtDemoFallback';
import { useContainerSize } from '../../hooks/useContainerSize';

const DEFAULT_SLOTS = getDTWidgetSlots('dt-radial-gauge');
const POLE_COLORS = ['#DC272D', '#FFD000', '#008CD7'];

function PoleGauge({ label, value, color, chartWidth, chartHeight }) {
  const w = Math.max(28, chartWidth);
  const h = Math.max(28, chartHeight);
  const valueFont = `${Math.max(10, Math.round(h * 0.13))}px`;
  const displayValue = `${Math.round(value)}%`;
  const valueCenterOffsetY = -Math.round(h * 0.12);

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        flex: '1 1 0',
        minWidth: 0,
        maxWidth: '100%',
        px: 0.5,
        minHeight: 0,
      }}
    >
      <Typography
        variant="caption"
        component="div"
        sx={{
          color,
          fontWeight: 700,
          textAlign: 'center',
          lineHeight: 1.25,
          mb: 0.5,
          width: '100%',
          overflow: 'hidden',
          display: '-webkit-box',
          WebkitLineClamp: 2,
          WebkitBoxOrient: 'vertical',
          wordBreak: 'break-word',
          flexShrink: 0,
        }}
        title={label}
      >
        {label}
      </Typography>
      <Box sx={{ position: 'relative', width: w, height: h, flexShrink: 0, lineHeight: 0 }}>
        <ReactApexChart
          options={{
            chart: {
              height: h,
              width: w,
              type: 'radialBar',
              background: 'transparent',
              toolbar: { show: false },
              sparkline: { enabled: true },
              parentHeightOffset: 0,
            },
            grid: { padding: { top: 0, right: 0, bottom: 0, left: 0 } },
            plotOptions: {
              radialBar: {
                hollow: { size: '58%' },
                track: { background: '#C1C1C1' },
                dataLabels: { name: { show: false }, value: { show: false } },
              },
            },
            colors: [color],
            labels: [''],
          }}
          series={[value]}
          type="radialBar"
          height={h}
          width={w}
        />
        <Box
          component="span"
          sx={{
            position: 'absolute',
            left: '50%',
            top: '50%',
            transform: `translate(-50%, calc(-50% + ${valueCenterOffsetY}px))`,
            margin: 0,
            padding: 0,
            color,
            fontWeight: 700,
            fontSize: valueFont,
            lineHeight: 1,
            pointerEvents: 'none',
            whiteSpace: 'nowrap',
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          {displayValue}
        </Box>
      </Box>
    </Box>
  );
}

function DTRadialGauge({ widget, data, theme }) {
  const tagprops = widget?.props?.extended?.tagprops || [];
  const liveData = Array.isArray(data) ? data : [];
  const slotValues = resolveSlotValues(tagprops, liveData);
  const demoMap = useDTDemoMap('dt-radial-gauge');
  const { ref: rowRef, width: rowW, height: rowH } = useContainerSize(160, 96);

  const poles = DEFAULT_SLOTS.map((slot, i) => {
    const resolved = slotValues[slot.key];
    const liveOk = hasLiveSlotValue(resolved);
    const num = liveOk ? parseFloat(resolved.value) : Number(demoMap[slot.key] ?? 0);
    return {
      label: resolved?.alias || slot.label,
      value: Number.isFinite(num) ? num : 0,
      color: POLE_COLORS[i] || '#008CD7',
    };
  });

  const labelBand = Math.min(44, Math.max(26, Math.round(rowH * 0.28)));
  const chartH = Math.max(36, rowH - labelBand);
  const colW = (rowW - 12) / 3;
  const chartW = Math.max(28, Math.min(colW - 8, chartH * 0.82));

  return (
    <Box sx={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column', width: '100%', height: '100%', overflow: 'hidden', p: 1, boxSizing: 'border-box' }}>
      <Typography variant="subtitle2" sx={{ color: theme === 'dark' ? '#fff' : '#000', fontWeight: 700, mb: 0.5, flexShrink: 0 }}>
        Contact Wear (%)
      </Typography>
      <Box
        ref={rowRef}
        sx={{
          flex: 1,
          minHeight: 0,
          display: 'flex',
          justifyContent: 'space-around',
          alignItems: 'stretch',
          gap: 0.5,
          overflow: 'hidden',
        }}
      >
        {poles.map((p, i) => (
          <PoleGauge key={`${p.label}-${i}`} label={p.label} value={p.value} color={p.color} chartWidth={chartW} chartHeight={chartH} />
        ))}
      </Box>
    </Box>
  );
}

export default React.memo(DTRadialGauge);
